/**
 * Quanto sono sicuri i numeri della pagina (Davide 27/10/2026: «quando un
 * dato e' incerto deve essere indicato»): certi, stimati, senza margine, e il
 * perche' di ogni stima, dal motivo salvato col margine (migr. 0134).
 */

import type { BiMotivo, BiTotaleEconomia } from "../biApi";
import { intero, motivo, n, perc } from "../biTesti";
import { InfoTip } from "./InfoTip";
import { Riquadro } from "./Riquadro";

export function Affidabilita({ e, motivi }: { e: BiTotaleEconomia; motivi: BiMotivo[] }) {
  const tot = Math.max(n(e.orders), 1);
  const parti = [
    {
      nome: "Certi",
      quanti: n(e.certain_count),
      colore: "bg-green",
      spiega:
        "Tutti i costi sono veri: conferma del fornitore, commissione vista, spedizione certa.",
    },
    {
      nome: "Stimati",
      quanti: n(e.estimate_count),
      colore: "bg-amber",
      spiega: "Il margine c'e', ma almeno un costo e' stimato. Vedi i motivi qui sotto.",
    },
    {
      nome: "Senza margine",
      quanti: n(e.not_computable_count),
      colore: "bg-red",
      spiega:
        "Manca un costo: il margine non si calcola e l'ordine resta fuori dal margine totale.",
    },
  ];
  const massimo = Math.max(...motivi.map((m) => n(m.orders)), 1);

  return (
    <Riquadro
      titolo="Quanto sono sicuri i numeri"
      spiegazione={{
        titolo: "Quanto sono sicuri i numeri",
        testo:
          "Ogni ordine ha un margine certo, stimato o assente. Un ordine puo' avere piu' motivi di stima, quindi i motivi sommati superano gli ordini.",
        conto: parti.map((p) => ({
          etichetta: p.nome,
          valore: `${intero(p.quanti)} su ${intero(e.orders)}`,
        })),
      }}
    >
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-paper-2" aria-hidden="true">
        {parti.map((p) => (
          <span
            key={p.nome}
            className={`bi-cresce h-full ${p.colore}`}
            style={{ width: `${(p.quanti / tot) * 100}%` }}
          />
        ))}
      </div>
      <ul className="mt-3 grid grid-cols-3 gap-3">
        {parti.map((p) => (
          <li key={p.nome} className="min-w-0">
            <span className="flex items-center gap-1.5 text-[12.5px] font-bold">
              <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-full ${p.colore}`} />
              {p.nome}
              <InfoTip
                spiegazione={{ titolo: `Ordini ${p.nome.toLowerCase()}`, testo: p.spiega }}
              />
            </span>
            <span className="mt-1 block font-display text-[24px] font-bold leading-none tabular-nums">
              {intero(p.quanti)}
            </span>
            <span className="text-[12px] font-semibold tabular-nums">
              {perc((p.quanti / tot) * 100, 0)}
            </span>
          </li>
        ))}
      </ul>

      <h3 className="mb-2 mt-5 text-[13px] font-bold">Perche' sono stimati</h3>
      {motivi.length === 0 ? (
        <p className="text-[13px]">Nessuna stima nel periodo.</p>
      ) : (
        <ul className="space-y-2">
          {motivi.map((m) => {
            const info = motivo(m.motivo);
            return (
              <li
                key={m.motivo}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1"
              >
                <span className="flex min-w-0 items-center gap-1.5 text-[13px] font-semibold">
                  {info.grave && (
                    <span
                      aria-label="toglie il margine"
                      className="h-2 w-2 shrink-0 rounded-full bg-red"
                    />
                  )}
                  <span className="truncate">{info.titolo}</span>
                  <InfoTip
                    spiegazione={{
                      titolo: info.titolo,
                      testo: info.spiega,
                      conto: [
                        { etichetta: "Ordini con questo motivo", valore: intero(m.orders) },
                        {
                          etichetta: "di cui senza margine",
                          valore: intero(m.not_computable_count),
                        },
                      ],
                    }}
                  />
                </span>
                <span className="text-[13px] font-bold tabular-nums">{intero(m.orders)}</span>
                <span
                  className="col-span-2 h-1.5 overflow-hidden rounded-full bg-paper-2"
                  aria-hidden="true"
                >
                  <span
                    className={`bi-cresce block h-full rounded-full ${info.grave ? "bg-red" : "bg-amber"}`}
                    style={{ width: `${(n(m.orders) / massimo) * 100}%` }}
                  />
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Riquadro>
  );
}
